/* istanbul ignore file */
import GraphicsLayerView from '@arcgis/core/views/layers/GraphicsLayerView';
import { MapViewInputManager } from 'cd-eft/business/MapViewInputManager/MapViewInputManager';
import { TextSketchInputProperties } from 'cd-eft/business/MapViewInputManager/TextSketchInputProperties';

import { computed, observable } from 'mobx';
import { DrawingsModel } from '../model/DrawingsModel';

export type DrawingToolType =
  | 'draw-delete'
  | 'draw-point'
  | 'draw-polyline'
  | 'draw-polygon'
  | 'draw-rectangle'
  | 'draw-circle'
  | 'draw-text';

export class DrawingToolViewModel {
  public readonly type: DrawingToolType;
  private readonly _inputManager: MapViewInputManager;
  private readonly _drawings: DrawingsModel;
  private readonly _textDrawingProps?: TextSketchInputProperties;

  @observable private _isActive: boolean;

  constructor(
    type: DrawingToolType,
    inputManager: MapViewInputManager,
    drawings: DrawingsModel,
    textDrawingProps?: TextSketchInputProperties
  ) {
    this.type = type;
    this._isActive = false;
    this._inputManager = inputManager;
    this._drawings = drawings;
    this._textDrawingProps = textDrawingProps;
  }

  @computed get isActive(): boolean {
    return this._isActive;
  }

  get textDrawingProps(): TextSketchInputProperties | undefined {
    return this._textDrawingProps;
  }

  async activateTool() {
    await this._inputManager.cancelCurrentOperationAsync();
    this._isActive = true;
    try {
      while (true) {
        // will run until execption is thrown due to tool cancellation

        switch (this.type) {
          case 'draw-delete':
            await this.deleteDrawing();
            break;
          case 'draw-point':
            await this.addPointDrawing();
            break;
          case 'draw-polyline':
            await this.addPolylineDrawing();
            break;
          case 'draw-polygon':
            await this.addPolygonDrawing();
            break;
          case 'draw-rectangle':
            await this.addRectangleDrawing();
            break;
          case 'draw-circle':
            await this.addCircleDrawing();
            break;
          case 'draw-text':
            await this.addTextDrawing();
            break;
          default:
            return;
        }
      }
    } catch (error) {
      if ((error as Error).message !== 'Action interrupted by user.') {
        // rethrow unexpected exception,
        throw error;
      }
    } finally {
      this._isActive = false;
    }
  }

  async deactivateTool() {
    if (this._isActive) {
      await this._inputManager.cancelCurrentOperationAsync();
    }
  }

  async toggleActivation() {
    if (this._isActive) {
      await this.deactivateTool();
    } else {
      await this.activateTool();
    }
  }

  private async addPointDrawing(): Promise<void> {
    const result = await this._inputManager.sketchPointAsync();
    this._drawings.addDrawing(result.generateGraphic(), result.type);
  }

  private async addPolylineDrawing(): Promise<void> {
    const result = await this._inputManager.sketchPolylineAsync();
    this._drawings.addDrawing(result.generateGraphic(), result.type);
  }

  private async addPolygonDrawing(): Promise<void> {
    const result = await this._inputManager.sketchPolygonAsync();
    this._drawings.addDrawing(result.generateGraphic(), result.type);
  }

  private async addRectangleDrawing(): Promise<void> {
    const result = await this._inputManager.sketchRectangleAsync();
    this._drawings.addDrawing(result.generateGraphic(), result.type);
  }

  private async addCircleDrawing(): Promise<void> {
    const result = await this._inputManager.sketchCircleAsync();
    this._drawings.addDrawing(result.generateGraphic(), result.type);
  }

  private async addTextDrawing(): Promise<void> {
    if (this._textDrawingProps) {
      const result = await this._inputManager.sketchTextAsync(this._textDrawingProps);
      if (result.text && result.text.trim().length > 0) {
        this._drawings.addDrawing(result.generateGraphic(), result.type);
        this._textDrawingProps.text = '';
      }
    }
  }

  private async deleteDrawing(): Promise<void> {
    let highlightHandle: any;

    try {
      const options: __esri.MapViewHitTestOptions = {};
      options.include = this._drawings.drawingsGroupLayer.layers;

      const highlightTopGraphic = async (e: any): Promise<void> => {
        if (highlightHandle) {
          highlightHandle.remove();
          highlightHandle = undefined;
        }
        const result = await this._inputManager.mapView.hitTest(e, options);
        if (result.results[0]) {
          const graphic = result.results[0].graphic;
          for (const layerView of this._inputManager.mapView.allLayerViews) {
            if (layerView.layer === graphic.layer) {
              highlightHandle = (layerView as GraphicsLayerView).highlight(graphic);
            }
          }
        }
      };

      const e = await this._inputManager.getMapViewImmediateClick('pointer', highlightTopGraphic);

      const result = await this._inputManager.mapView.hitTest(e, options);

      if (result.results[0]) {
        const graphic = result.results[0].graphic;
        this._drawings.deleteDrawing(graphic);
      }
    } finally {
      // make sure to cleanup the handle
      if (highlightHandle) {
        highlightHandle.remove();
        highlightHandle = undefined;
      }
    }
  }
}
